/**
 * HTML Tags
 * @see https://developer.mozilla.org/en-US/docs/Web/HTML/Element
 */
enum HtmlTags {
  A = 'a',
  ABBR = 'abbr',
  ACRONYM = 'acronym', // deprecated
  ADDRESS = 'address',
  AREA = 'area',
  ARTICLE = 'article',
  ASIDE = 'aside',
  AUDIO = 'audio',
  B = 'b',
  BDI = 'bdi',
  BDO = 'bdo',
  BIG = 'big', // deprecated
  BLOCKQUOTE = 'blockquote',
  BODY = 'body',
  BR = 'br',
  BUTTON = 'button',
  CAPTION = 'caption',
  CENTER = 'center', // deprecated
  CITE = 'cite',
  CODE = 'code',
  COL = 'col',
  COLGROUP = 'colgroup',
  DATA = 'data',
  DATALIST = 'datalist',
  DD = 'dd',
  DEL = 'del',
  DETAILS = 'details',
  DFN = 'dfn',
  DIR = 'dir', // deprecated
  DIV = 'div',
  DL = 'dl',
  DT = 'dt',
  EM = 'em',
  FIELDSET = 'fieldset',
  FIGCAPTION = 'figcaption',
  FIGURE = 'figure',
  FONT = 'font', // deprecated
  FOOTER = 'footer',
  FORM = 'form',
  H1 = 'h1',
  H2 = 'h2',
  H3 = 'h3',
  H4 = 'h4',
  H5 = 'h5',
  H6 = 'h6',
  HEAD = 'head',
  HEADER = 'header',
  HGROUP = 'hgroup',
  HR = 'hr',
  HTML = 'html',
  I = 'i',
  IMG = 'img',
  INPUT = 'input',
  INS = 'ins',
  KBD = 'kbd',
  LABEL = 'label',
  LEGEND = 'legend',
  LI = 'li',
  LINK = 'link',
  MAIN = 'main',
  MAP = 'map',
  MARK = 'mark',
  MENU = 'menu',
  META = 'meta',
  METER = 'meter',
  NAV = 'nav',
  OL = 'ol',
  OPTGROUP = 'optgroup',
  OPTION = 'option',
  P = 'p',
  PICTURE = 'picture',
  PRE = 'pre',
  PROGRESS = 'progress',
  Q = 'q',
  RP = 'rp',
  RT = 'rt',
  RUBY = 'ruby',
  S = 's',
  SAMP = 'samp',
  SCRIPT = 'script',
  SECTION = 'section',
  SELECT = 'select',
  SMALL = 'small',
  SOURCE = 'source',
  SPAN = 'span',
  STRIKE = 'strike', // deprecated
  STRONG = 'strong',
  STYLE = 'style',
  SUB = 'sub',
  SUMMARY = 'summary',
  SUP = 'sup',
  SVG = 'svg',
  TABLE = 'table',
  TBODY = 'tbody',
  TD = 'td',
  TEXTAREA = 'textarea',
  TFOOT = 'tfoot',
  TH = 'th',
  THEAD = 'thead',
  TIME = 'time',
  TITLE = 'title',
  TR = 'tr',
  TRACK = 'track',
  TT = 'tt', // deprecated
  U = 'u',
  UL = 'ul',
  VAR = 'var',
  VIDEO = 'video',
  WBR = 'wbr',
}

// source: https://developer.mozilla.org/en-US/docs/Web/HTML/Element
// all event attributes are purposely ommitted
const globalAttrs =
  'accessKey autocapitalize autofocus class contenteditable dir enterkeyhint hidden id inputmode itemid itemprop itemref itemscope itemtype lang part role spellcheck style tabindex title translate'.split(
    ' ',
  ); // removed (deprecated): contextmenu; removed (not applicable to our use case): draggable is nonce slot; added but against my better judgement: contenteditable

const newBlockHtmlAttrs = {
  // not included in TinyMCE
};

const newSpanHtmlAttrs = {
  // not included in TinyMCE
  [HtmlTags.DATA]: [...globalAttrs, ...'value'.split(' ')],
};

// build attributes for each tag based on docs
const htmlAttrs = {
  ...newBlockHtmlAttrs,
  ...newSpanHtmlAttrs,
  [HtmlTags.A]: [
    ...globalAttrs,
    ...'download href hreflang ping referrerpolicy rel target type'.split(' '),
    ...'charset coords name rev shape'.split(' '), // deprecated
    ...'media'.split(' '), // added by Tiny
  ],
  [HtmlTags.ABBR]: [...globalAttrs],
  [HtmlTags.ACRONYM]: [...globalAttrs],
  [HtmlTags.ADDRESS]: [...globalAttrs],
  [HtmlTags.AREA]: [
    ...globalAttrs,
    ...'alt coords download href hreflang ping referrerpolicy rel shape target'.split(' '),
    ...'name nohref type'.split(' '), // deprecated
    ...'media'.split(' '), // added by Tiny
  ],
  [HtmlTags.ARTICLE]: [...globalAttrs],
  [HtmlTags.ASIDE]: [...globalAttrs],
  [HtmlTags.AUDIO]: [
    ...globalAttrs,
    ...'autoplay controls controlslist crossorigin disableremoteplayback loop muted preload src'.split(
      ' ',
    ),
    ...'buffered mediagroup volume'.split(' '), // added by Tiny
  ],
  [HtmlTags.B]: [...globalAttrs],
  [HtmlTags.BDI]: [...globalAttrs],
  [HtmlTags.BDO]: [...globalAttrs],
  [HtmlTags.BIG]: [...globalAttrs],
  [HtmlTags.BLOCKQUOTE]: [...globalAttrs, ...'cite'.split(' ')],
  [HtmlTags.BODY]: [
    ...globalAttrs,
    ...'alink background bgcolor bottommargin leftmargin link rightmargin text topmargin vlink'.split(
      ' ',
    ), // deprecated
    ...'marginheight'.split(' '), // added by BE Spike
  ],
  [HtmlTags.BR]: [
    ...globalAttrs,
    ...'clear'.split(' '), // deprecated
  ],
  [HtmlTags.BUTTON]: [
    ...globalAttrs,
    ...'autofocus disabled form formaction formenctype formmethod formnovalidate formtarget name type value'.split(
      ' ',
    ),
    ...'autocomplete'.split(' '), // deprecated
  ],
  [HtmlTags.CAPTION]: [
    ...globalAttrs,
    ...'align'.split(' '), // deprecated
  ],
  [HtmlTags.CENTER]: [...globalAttrs],
  [HtmlTags.CITE]: [...globalAttrs],
  [HtmlTags.CODE]: [...globalAttrs],
  [HtmlTags.COL]: [
    ...globalAttrs,
    ...'span'.split(' '),
    ...'align bgcolor char charoff valign width'.split(' '), // deprecated
  ],
  [HtmlTags.COLGROUP]: [
    ...globalAttrs,
    ...'span'.split(' '),
    ...'align bgcolor char charoff valign'.split(' '), // deprecated
    ...'width'.split(' '), // added by Tiny
  ],
  [HtmlTags.DATALIST]: [...globalAttrs],
  [HtmlTags.DD]: [
    ...globalAttrs,
    ...'nowrap'.split(' '), // deprecated
  ],
  [HtmlTags.DEL]: [...globalAttrs, ...'cite datetime'.split(' ')],
  [HtmlTags.DETAILS]: [...globalAttrs, ...'open'.split(' ')],
  [HtmlTags.DFN]: [...globalAttrs, ...'title'.split(' ')],
  [HtmlTags.DIR]: [
    ...globalAttrs,
    ...'compact'.split(' '), // deprecated
  ],
  [HtmlTags.DIV]: [
    ...globalAttrs,
    ...'align'.split(' '), // added by Tiny
  ],
  [HtmlTags.DL]: [
    ...globalAttrs,
    ...'compact'.split(' '), // added by Tiny
  ],
  [HtmlTags.DT]: [...globalAttrs],
  [HtmlTags.EM]: [...globalAttrs],
  [HtmlTags.FIELDSET]: [...globalAttrs, ...'disabled form name'.split(' ')],
  [HtmlTags.FIGCAPTION]: [...globalAttrs],
  [HtmlTags.FIGURE]: [...globalAttrs],
  [HtmlTags.FONT]: [
    ...globalAttrs,
    ...'color face size'.split(' '), // deprecated
  ],
  [HtmlTags.FOOTER]: [...globalAttrs],
  [HtmlTags.FORM]: [
    ...globalAttrs,
    ...'accept-charset action autocomplete enctype method name novalidate rel target'.split(' '),
    ...'accept'.split(' '), // deprecated
  ],
  [HtmlTags.H1]: [
    ...globalAttrs,
    ...'align'.split(' '), // added by Tiny
  ],
  [HtmlTags.H2]: [
    ...globalAttrs,
    ...'align'.split(' '), // added by Tiny
  ],
  [HtmlTags.H3]: [
    ...globalAttrs,
    ...'align'.split(' '), // added by Tiny
  ],
  [HtmlTags.H4]: [
    ...globalAttrs,
    ...'align'.split(' '), // added by Tiny
  ],
  [HtmlTags.H5]: [
    ...globalAttrs,
    ...'align'.split(' '), // added by Tiny
  ],
  [HtmlTags.H6]: [
    ...globalAttrs,
    ...'align'.split(' '), // added by Tiny
  ],
  [HtmlTags.HEAD]: [
    ...globalAttrs,
    ...'profile'.split(' '), // deprecated
  ],
  [HtmlTags.HEADER]: [...globalAttrs],
  [HtmlTags.HGROUP]: [...globalAttrs],
  [HtmlTags.HR]: [
    ...globalAttrs,
    ...'align color noshade size width'.split(' '), // deprecated
  ],
  [HtmlTags.HTML]: [
    ...globalAttrs,
    ...'xmlns'.split(' '),
    ...'manifest version'.split(' '), // deprecated
    ...'lang xmlns:o'.split(' '), // added by BE Spike
  ],
  [HtmlTags.I]: [...globalAttrs],
  [HtmlTags.IMG]: [
    ...globalAttrs,
    ...'alt crossorigin decoding fetchpriority height ismap loading referrerpolicy sizes src srcset width usemap'.split(
      ' ',
    ),
    ...'align border hspace longdesc name vspace'.split(' '), // deprecated
    ...'title'.split(' '), // added by BE Spike
  ],
  [HtmlTags.INPUT]: [
    ...globalAttrs,
    ...'accept alt autocomplete autofocus capture checked dirname disabled form formaction formenctype formmethod formnovalidate formtarget height list max maxlength min minlength multiple name pattern placeholder readonly required size src step type value width'.split(
      ' ',
    ),
    ...'align usemap'.split(' '), // added by Tiny
  ],
  [HtmlTags.INS]: [...globalAttrs, ...'cite datetime'.split(' ')],
  [HtmlTags.KBD]: [...globalAttrs],
  [HtmlTags.LABEL]: [
    ...globalAttrs,
    ...'for'.split(' '),
    ...'form'.split(' '), // added by Tiny
  ],
  [HtmlTags.LEGEND]: [
    ...globalAttrs,
    ...'align'.split(' '), // added by Tiny
  ],
  [HtmlTags.LI]: [
    ...globalAttrs,
    ...'value'.split(' '),
    ...'type'.split(' '), // deprecated
  ],
  [HtmlTags.LINK]: [
    ...globalAttrs,
    ...'as crossorigin fetchpriority href hreflang imagesizes imagesrcset integrity media prefetch referrerpolicy rel sizes title type'.split(
      ' ',
    ),
    ...'disabled'.split(' '), // deprecated
  ],
  [HtmlTags.MAIN]: [...globalAttrs],
  [HtmlTags.MAP]: [...globalAttrs, ...'name'.split(' ')],
  [HtmlTags.MARK]: [...globalAttrs],
  [HtmlTags.MENU]: [
    ...globalAttrs,
    ...'compact label type'.split(' '), // added by Tiny
  ],
  [HtmlTags.META]: [...globalAttrs, ...'charset content http-equiv name'.split(' ')],
  [HtmlTags.METER]: [...globalAttrs, ...'value min max low high optimum'.split(' ')],
  [HtmlTags.NAV]: [...globalAttrs],
  [HtmlTags.OL]: [
    ...globalAttrs,
    ...'reversed start type'.split(' '),
    ...'compact'.split(' '), // added by Tiny
  ],
  [HtmlTags.OPTGROUP]: [...globalAttrs, ...'disabled label'.split(' ')],
  [HtmlTags.OPTION]: [...globalAttrs, ...'disabled label selected value'.split(' ')],
  [HtmlTags.P]: [
    ...globalAttrs,
    ...'align'.split(' '), // added by Tiny
  ],
  [HtmlTags.PICTURE]: [...globalAttrs],
  [HtmlTags.PRE]: [
    ...globalAttrs,
    ...'cols width wrap'.split(' '), // deprecated
    ...'xml:space'.split(' '), // added by Tiny
  ],
  [HtmlTags.PROGRESS]: [...globalAttrs, ...'max value'.split(' ')],
  [HtmlTags.Q]: [...globalAttrs, ...'cite'.split(' ')],
  [HtmlTags.RP]: [...globalAttrs],
  [HtmlTags.RT]: [...globalAttrs],
  [HtmlTags.RUBY]: [...globalAttrs],
  [HtmlTags.S]: [...globalAttrs],
  [HtmlTags.SAMP]: [...globalAttrs],
  [HtmlTags.SCRIPT]: [
    ...globalAttrs,
    ...'async crossorigin defer fetchpriority integrity nomodule nonce referrerpolicy src type'.split(
      ' ',
    ),
    ...'charset language'.split(' '), // deprecated
  ],
  [HtmlTags.SECTION]: [...globalAttrs],
  [HtmlTags.SELECT]: [
    ...globalAttrs,
    ...'autocomplete autofocus disabled form multiple name required size'.split(' '),
  ],
  [HtmlTags.SMALL]: [...globalAttrs],
  [HtmlTags.SOURCE]: [...globalAttrs, ...'type src srcset sizes media height width'.split(' ')],
  [HtmlTags.SPAN]: [...globalAttrs],
  [HtmlTags.STRIKE]: [
    // will be automatically converted to <s> by TinyMCE
    ...globalAttrs,
  ],
  [HtmlTags.STRONG]: [...globalAttrs],
  [HtmlTags.STYLE]: [
    ...globalAttrs,
    ...'media nonce title'.split(' '),
    ...'type'.split(' '),
    ...'scoped xml:space'.split(' '), // added by Tiny
  ],
  [HtmlTags.SUB]: [...globalAttrs],
  [HtmlTags.SUMMARY]: [...globalAttrs],
  [HtmlTags.SUP]: [...globalAttrs],
  [HtmlTags.SVG]: [
    ...globalAttrs,
    ...'xmlns xmlns:xlink height preserveAspectRatio viewBox width x y clip-path clip-rule color color-interpolation color-rendering cursor display fill fill-opacity fill-rule filter mask opacity pointer-events shape-rendering stroke stroke-dasharray stroke-dashoffset stroke-linecap stroke-linejoin stroke-miterlimit stroke-opacity stroke-width transform vector-effect visibility'.split(
      ' ',
    ),
  ],
  [HtmlTags.TABLE]: [
    ...globalAttrs,
    ...'align bgcolor border cellpadding cellspacing frame rules summary width'.split(' '), // deprecated
    ...'background'.split(' '), // added by Iterable
    ...'height'.split(' '), // added by BE analysis
  ],
  [HtmlTags.TBODY]: [
    ...globalAttrs,
    ...'align bgcolor char charoff valign'.split(' '), // deprecated
  ],
  [HtmlTags.TD]: [
    ...globalAttrs,
    ...'colspan headers rowspan'.split(' '),
    ...'abbr align axis bgcolor char charoff height scope valign width'.split(' '), // deprecated
    ...'background'.split(' '), // added by Iterable
    ...'nowrap'.split(' '), // added by Tiny
  ],
  [HtmlTags.TEXTAREA]: [
    ...globalAttrs,
    ...'autocomplete autofocus cols disabled form maxlength minlength name placeholder readonly required rows spellcheck wrap'.split(
      ' ',
    ),
    ...'autocorrect'.split(' '), // non-standard
    ...'dirname'.split(' '), // added by Tiny
  ],
  [HtmlTags.TFOOT]: [
    ...globalAttrs,
    ...'align bgcolor char charoff valign'.split(' '), // deprecated
  ],
  [HtmlTags.TH]: [
    ...globalAttrs,
    ...'abbr colspan headers rowspan scope'.split(' '),
    ...'align axis bgcolor char charoff height valign width'.split(' '), // deprecated
    ...'background'.split(' '), // added by Iterable
    ...'nowrap'.split(' '), // added by Tiny
    ...'bicolor'.split(' '), // added by BE Spike
  ],
  [HtmlTags.THEAD]: [
    ...globalAttrs,
    ...'align bgcolor char charoff valign'.split(' '), // deprecated
  ],
  [HtmlTags.TIME]: [...globalAttrs, ...'datetime'.split(' ')],
  [HtmlTags.TITLE]: [...globalAttrs],
  [HtmlTags.TR]: [
    ...globalAttrs,
    ...'align bgcolor char charoff valign'.split(' '), // deprecated
    ...'height'.split(' '), // added by BE analysis
  ],
  [HtmlTags.TRACK]: [...globalAttrs, ...'default kind label src srclang'.split(' ')],
  [HtmlTags.TT]: [...globalAttrs],
  [HtmlTags.U]: [...globalAttrs],
  [HtmlTags.UL]: [
    ...globalAttrs,
    ...'compact type'.split(' '), // deprecated
    ...'align'.split(' '), // added by BE analysis
  ],
  [HtmlTags.VAR]: [...globalAttrs],
  [HtmlTags.VIDEO]: [
    ...globalAttrs,
    ...'autoplay autopictureinpicture controls crossorigin disablepictureinpicture disableremoteplayback height loop muted playsinline poster preload src width'.split(
      ' ',
    ),
    ...'controlslist'.split(' '), // non-standard
    ...'buffered mediagroup'.split(' '), // added by Tiny
  ],
  [HtmlTags.WBR]: [...globalAttrs],
};

export const newBlockTags = Object.keys(newBlockHtmlAttrs).sort();
export const newSpanTags = Object.keys(newSpanHtmlAttrs).sort();
export const htmlTags = Object.keys(htmlAttrs).sort();
export const htmlElements = (Object.keys(HtmlTags) as Array<keyof typeof HtmlTags>).map(
  (key) => `${HtmlTags[key]}[${[...new Set(htmlAttrs[HtmlTags[key]])].sort().join('|')}]`, // unique, sorted attributes
);
